import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Kpi";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, RefreshCw, Plus, Trash2, Copy, Clock, Activity, Webhook, AlertCircle, Mail, Send } from "lucide-react";

const INTERVAL_OPTIONS = [
    { v: 15, l: "Every 15 min" },
    { v: 30, l: "Every 30 min" },
    { v: 60, l: "Every hour" },
    { v: 120, l: "Every 2 hours" },
    { v: 240, l: "Every 4 hours" },
    { v: 360, l: "Every 6 hours" },
    { v: 720, l: "Every 12 hours" },
    { v: 1440, l: "Once a day" },
];
const WINDOW_OPTIONS = [
    { v: 1, l: "Last 1 day (just stock & balances refresh)" },
    { v: 7, l: "Last 7 days" },
    { v: 30, l: "Last 30 days" },
    { v: 90, l: "Last 90 days" },
];

export default function Settings() {
    const [status, setStatus] = useState(null);
    const [keyInput, setKeyInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyName, setNewKeyName] = useState("");
    const [creatingKey, setCreatingKey] = useState(false);
    const [createdKey, setCreatedKey] = useState(null);
    const [autoSync, setAutoSync] = useState({ enabled: false, interval_minutes: 60, recent_days: 7 });
    const [savingAuto, setSavingAuto] = useState(false);
    const [rateLimits, setRateLimits] = useState([]);
    const [webhook, setWebhook] = useState(null);
    const [webhookSecret, setWebhookSecret] = useState("");
    const [savingWebhook, setSavingWebhook] = useState(false);
    const [deliveries, setDeliveries] = useState([]);
    const [webhookBaseUrl, setWebhookBaseUrl] = useState("");
    const [resend, setResend] = useState(null);
    const [resendForm, setResendForm] = useState({ api_key: "", sender_email: "", app_url: "", reply_to: "" });
    const [savingResend, setSavingResend] = useState(false);
    const [testResendTo, setTestResendTo] = useState("");
    const [testingResend, setTestingResend] = useState(false);
    const [savingUrl, setSavingUrl] = useState(false);

    const loadStatus = () => api.get("/takealot/status").then((r) => {
        setStatus(r.data);
        if (r.data.auto_sync) setAutoSync(r.data.auto_sync);
    });
    const loadApiKeys = () => api.get("/api-keys").then((r) => setApiKeys(r.data.items || []));
    const loadRateLimits = () => api.get("/admin/rate-limits").then((r) => setRateLimits(r.data.items || [])).catch(() => {});
    const loadWebhook = () => api.get("/admin/webhook").then((r) => {
        setWebhook(r.data);
        setWebhookBaseUrl(r.data?.custom_base_url || "");
    }).catch(() => {});
    const loadDeliveries = () => api.get("/admin/webhook/deliveries").then((r) => setDeliveries(r.data.items || [])).catch(() => {});
    const loadResend = () => api.get("/admin/integrations/resend").then((r) => {
        setResend(r.data);
        if (r.data) {
            setResendForm({
                api_key: "",
                sender_email: r.data.sender_email || "",
                app_url: r.data.app_url || "",
                reply_to: r.data.reply_to || "",
            });
        }
    }).catch(() => {});

    const saveResend = async (e) => {
        e?.preventDefault();
        if (!resendForm.sender_email || !resendForm.app_url) {
            toast.error("Sender email and app URL are required");
            return;
        }
        const payload = { ...resendForm };
        if (!payload.api_key) {
            if (!resend?.configured) {
                toast.error("Paste your Resend API key");
                return;
            }
            // Keep the existing key on the server by not sending api_key
            delete payload.api_key;
        }
        // Backend requires api_key non-empty on PUT — if we removed it, refuse and prompt
        if (!payload.api_key) {
            toast.error("To update sender email / app URL you must also paste the current API key (Resend doesn't let us fetch it back).");
            return;
        }
        setSavingResend(true);
        try {
            await api.put("/admin/integrations/resend", payload);
            toast.success("Resend saved");
            setResendForm({ ...resendForm, api_key: "" });
            await loadResend();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingResend(false);
        }
    };

    const testResend = async () => {
        if (!testResendTo) {
            toast.error("Enter a test recipient email");
            return;
        }
        setTestingResend(true);
        try {
            const r = await api.post("/admin/integrations/resend/test", { to: testResendTo });
            toast.success(`Test email sent (id ${r.data.email_id || "—"}). Check the inbox.`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTestingResend(false);
        }
    };

    const webhookFullUrl = webhook?.webhook_url || `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/takealot`;

    const saveWebhookSecret = async () => {
        if (!webhookSecret.trim() || webhookSecret.length < 8) {
            toast.error("Secret must be at least 8 characters");
            return;
        }
        setSavingWebhook(true);
        try {
            await api.put("/admin/webhook", { secret: webhookSecret.trim() });
            toast.success("Webhook secret saved");
            setWebhookSecret("");
            await loadWebhook();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingWebhook(false);
        }
    };

    const clearWebhookSecret = async () => {
        if (!window.confirm("Remove the webhook signature secret? Incoming webhooks will no longer be verified.")) return;
        try {
            await api.delete("/admin/webhook");
            toast.success("Webhook secret removed");
            await loadWebhook();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const saveWebhookUrl = async () => {
        const url = webhookBaseUrl.trim().replace(/\/+$/, "");
        if (!url) {
            // empty → clear custom URL (use auto-detected)
            try {
                await api.delete("/admin/webhook/url");
                toast.success("Reverted to auto-detected URL");
                await loadWebhook();
            } catch (err) {
                toast.error(formatApiError(err));
            }
            return;
        }
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
            toast.error("URL must start with https://");
            return;
        }
        setSavingUrl(true);
        try {
            await api.put("/admin/webhook/url", { base_url: url });
            toast.success("Webhook URL updated — copy the new URL into Takealot");
            await loadWebhook();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingUrl(false);
        }
    };

    useEffect(() => {
        loadStatus().catch((e) => toast.error(formatApiError(e)));
        loadApiKeys().catch(() => {});
        loadRateLimits();
        loadWebhook();
        loadDeliveries();
        loadResend();
    }, []);

    const saveTakealotKey = async (e) => {
        e?.preventDefault();
        if (!keyInput.trim() || keyInput.length < 20) {
            toast.error("Please paste a valid Takealot API key");
            return;
        }
        setSaving(true);
        try {
            await api.patch("/admin/takealot-key", { api_key: keyInput.trim() });
            toast.success("Takealot API key updated");
            setKeyInput("");
            await loadStatus();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const doSync = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/takealot/sync?all_time=true");
            if (data.started) toast.success("All-time sync started in the background — refresh in 30-90s.");
            else toast.info(data.reason);
            await loadStatus();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSyncing(false);
        }
    };

    const createApiKey = async (e) => {
        e?.preventDefault();
        if (!newKeyName.trim()) return;
        setCreatingKey(true);
        try {
            const { data } = await api.post("/api-keys", { name: newKeyName.trim() });
            setCreatedKey(data);
            setNewKeyName("");
            await loadApiKeys();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCreatingKey(false);
        }
    };

    const revokeKey = async (id) => {
        if (!window.confirm("Revoke this API key? Any apps using it will lose access immediately.")) return;
        try {
            await api.delete(`/api-keys/${id}`);
            toast.success("Key revoked");
            await loadApiKeys();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    const saveAutoSync = async () => {
        setSavingAuto(true);
        try {
            await api.patch("/admin/auto-sync", autoSync);
            toast.success(autoSync.enabled ? "Auto-sync enabled" : "Auto-sync disabled");
            await loadStatus();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingAuto(false);
        }
    };

    return (
        <div>
            <PageHeader title="Settings" subtitle="Manage your Takealot connection, external API access, and account" />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <div data-testid="api-status-card" className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-6 rounded-sm shadow-sm">
                    <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-text-dim)]">
                        <KeyRound className="w-4 h-4" />
                        <div className="text-xs uppercase tracking-[0.18em]">Takealot API key</div>
                    </div>
                    {status?.configured ? (
                        <div data-testid="api-key-status" className="mb-4">
                            <div className="flex items-center gap-2 text-[color:var(--bl-success)] mb-2">
                                <CheckCircle2 className="w-4 h-4" /> Configured
                            </div>
                            <div data-testid="api-key-preview" className="font-mono-num text-sm bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 rounded-sm inline-block">
                                {status.key_preview}
                            </div>
                            <div className="text-[10px] text-[color:var(--bl-text-mute)] mt-2">
                                Source: {status.key_source}
                            </div>
                        </div>
                    ) : (
                        <div className="text-[color:var(--bl-danger)] mb-3">Not configured</div>
                    )}
                    <form onSubmit={saveTakealotKey} className="mt-2">
                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">
                            Update key
                        </label>
                        <div className="flex gap-2">
                            <input
                                data-testid="takealot-key-input"
                                type="password"
                                placeholder="Paste new Takealot API key"
                                value={keyInput}
                                onChange={(e) => setKeyInput(e.target.value)}
                                className="flex-1 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm font-mono-num focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                            />
                            <button
                                data-testid="save-takealot-key-btn"
                                type="submit"
                                disabled={saving}
                                className="bg-[color:var(--bl-takealot-blue)] text-white px-4 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50"
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                        <p className="text-[11px] text-[color:var(--bl-text-mute)] mt-2 leading-relaxed">
                            Generated in Takealot Seller Portal → API Integrations → Authentication. Stored encrypted server-side. Key only shown once on Takealot's portal, so paste it here when rotating.
                        </p>
                    </form>
                </div>

                <div data-testid="sync-control-card" className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-6 rounded-sm shadow-sm">
                    <div className="flex items-center gap-2 mb-3 text-[color:var(--bl-text-dim)]">
                        <RefreshCw className="w-4 h-4" />
                        <div className="text-xs uppercase tracking-[0.18em]">Data sync</div>
                    </div>
                    <p className="text-sm text-[color:var(--bl-text-dim)] mb-2">
                        Pulls <span className="font-semibold text-[color:var(--bl-text)]">all-time</span> sales, transactions, returns, plus offers, shipments, balances and seller info from Takealot.
                    </p>
                    {status?.job?.status === "in_progress" && (
                        <div className="text-xs text-[color:var(--bl-takealot-blue)] mb-2 inline-flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 animate-spin" /> Sync in progress…
                        </div>
                    )}
                    {status?.job?.status === "completed" && (
                        <div className="text-xs text-[color:var(--bl-success)] mb-2">
                            Last completed: {status.job.completed_at?.replace("T", " ").slice(0, 19)}
                        </div>
                    )}
                    {status?.job?.status === "error" && (
                        <div className="text-xs text-[color:var(--bl-danger)] mb-2">Error: {status.job.error}</div>
                    )}
                    <button
                        data-testid="settings-sync-btn"
                        onClick={doSync}
                        disabled={syncing || status?.job?.status === "in_progress"}
                        className="bg-[color:var(--bl-takealot-blue)] text-white px-4 py-2 rounded-sm text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        <RefreshCw className={`w-4 h-4 ${syncing || status?.job?.status === "in_progress" ? "animate-spin" : ""}`} />
                        Sync all-time
                    </button>
                </div>
            </div>

            <div data-testid="auto-sync-card" className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-6 rounded-sm shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-2 text-[color:var(--bl-text-dim)]">
                    <Clock className="w-4 h-4" />
                    <div className="text-xs uppercase tracking-[0.18em]">Scheduled auto-sync</div>
                </div>
                <p className="text-sm text-[color:var(--bl-text-dim)] mb-4">
                    Automatically pull fresh data on a schedule. Recommended: every 1-2 hours with a 7-day window to keep stock and recent orders current without hammering the Takealot API.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-3">
                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">Status</label>
                        <button
                            data-testid="auto-sync-toggle"
                            type="button"
                            onClick={() => setAutoSync((a) => ({ ...a, enabled: !a.enabled }))}
                            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-sm border transition ${
                                autoSync.enabled
                                    ? "bg-[color:var(--bl-success)] text-white border-[color:var(--bl-success)]"
                                    : "bg-[color:var(--bl-bg)] text-[color:var(--bl-text-dim)] border-[color:var(--bl-border)]"
                            }`}>
                            <span className={`w-2 h-2 rounded-full ${autoSync.enabled ? "bg-white" : "bg-[color:var(--bl-text-dim)]"}`} />
                            {autoSync.enabled ? "Enabled" : "Disabled"}
                        </button>
                    </div>
                    <div className="md:col-span-4">
                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">Interval</label>
                        <select data-testid="auto-sync-interval" value={autoSync.interval_minutes}
                            onChange={(e) => setAutoSync({ ...autoSync, interval_minutes: parseInt(e.target.value, 10) })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            {INTERVAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">Window</label>
                        <select data-testid="auto-sync-window" value={autoSync.recent_days}
                            onChange={(e) => setAutoSync({ ...autoSync, recent_days: parseInt(e.target.value, 10) })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]">
                            {WINDOW_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <button data-testid="auto-sync-save" onClick={saveAutoSync} disabled={savingAuto}
                            className="w-full bg-[color:var(--bl-takealot-blue)] text-white px-3 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50">
                            {savingAuto ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
                {status?.last_scheduled_at && (
                    <div className="mt-3 text-xs text-[color:var(--bl-text-dim)] flex gap-4">
                        <span data-testid="auto-sync-last">Last scheduled run: {status.last_scheduled_at.replace("T", " ").slice(0, 19)}</span>
                        {status.next_scheduled_at && (
                            <span data-testid="auto-sync-next">Next: {status.next_scheduled_at.replace("T", " ").slice(0, 19)}</span>
                        )}
                    </div>
                )}
                <p className="mt-3 text-[11px] text-[color:var(--bl-text-mute)] leading-relaxed">
                    Note: Takealot's docs recommend webhooks over polling — auto-sync is a pragmatic alternative until webhooks are wired up. Stock and balances are always refreshed; the window controls how far back to re-pull sales/transactions/returns to catch status changes.
                </p>
            </div>

            <div data-testid="rate-limit-card" className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-6 rounded-sm shadow-sm mb-6">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-[color:var(--bl-text-dim)]">
                        <Activity className="w-4 h-4" />
                        <div className="text-xs uppercase tracking-[0.18em]">Takealot API rate limits</div>
                    </div>
                    <button data-testid="rate-limit-refresh" onClick={loadRateLimits} className="text-xs text-[color:var(--bl-takealot-blue)] hover:underline inline-flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                </div>
                <p className="text-xs text-[color:var(--bl-text-dim)] mb-3 leading-relaxed">
                    These numbers are captured from <code className="font-mono-num">X-RateLimit-*</code> headers on every Takealot API response, per endpoint. Takealot's rate limits are dynamic — values vary by endpoint and system load.
                </p>
                {rateLimits.length === 0 ? (
                    <div className="text-sm text-[color:var(--bl-text-dim)] py-4 text-center">No rate-limit data yet — trigger a sync first.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="rate-limit-table">
                        <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                            <tr>
                                <th className="text-left py-2">Endpoint</th>
                                <th className="text-right py-2">Limit</th>
                                <th className="text-right py-2">Remaining</th>
                                <th className="text-right py-2">Headroom</th>
                                <th className="text-left py-2">Resets at</th>
                                <th className="text-left py-2">Observed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rateLimits.map((r) => {
                                const headroom = r.limit ? (r.remaining / r.limit) * 100 : null;
                                const headroomColor = headroom == null
                                    ? "text-[color:var(--bl-text-dim)]"
                                    : headroom > 50 ? "text-[color:var(--bl-success)]"
                                    : headroom > 20 ? "text-[color:var(--bl-warning)]"
                                    : "text-[color:var(--bl-danger)]";
                                let resetStr = "—";
                                if (r.reset) {
                                    const numeric = Number(r.reset);
                                    if (!isNaN(numeric) && numeric > 0) {
                                        const ms = numeric > 1e12 ? numeric : numeric * 1000;
                                        resetStr = new Date(ms).toISOString().slice(0, 19).replace("T", " ");
                                    } else {
                                        resetStr = String(r.reset).slice(0, 19).replace("T", " ");
                                    }
                                }
                                return (
                                    <tr key={r.endpoint} className="border-b border-[color:var(--bl-border)]">
                                        <td className="py-2 font-mono-num text-xs">{r.endpoint}</td>
                                        <td className="py-2 text-right font-mono-num">{r.limit ?? "—"}</td>
                                        <td className="py-2 text-right font-mono-num">{r.remaining ?? "—"}</td>
                                        <td className={`py-2 text-right font-mono-num ${headroomColor}`}>{headroom != null ? `${headroom.toFixed(0)}%` : "—"}</td>
                                        <td className="py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{resetStr}</td>
                                        <td className="py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{(r.observed_at || "").slice(0, 19).replace("T", " ")}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <div data-testid="webhook-card" className="border border-[color:var(--bl-takealot-blue)]/40 bg-[color:var(--bl-takealot-blue)]/5 p-6 rounded-sm shadow-sm mb-6">
                <div className="flex items-start gap-3 mb-4">
                    <Webhook className="w-5 h-5 text-[color:var(--bl-takealot-blue)] mt-1 shrink-0" />
                    <div className="flex-1">
                        <div className="font-serif-display text-xl text-[color:var(--bl-text)] mb-1">Takealot webhook receiver</div>
                        <p className="text-sm text-[color:var(--bl-text-dim)]">
                            Paste this URL and secret into Takealot Seller Portal → API Integrations → Webhooks. Incoming events update your data in real time — no polling needed.
                        </p>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">
                        Public base URL{" "}
                        {webhook?.custom_base_url
                            ? <span className="text-[color:var(--bl-takealot-orange)] normal-case">· custom override active</span>
                            : <span className="text-[color:var(--bl-text-mute)] normal-case">· auto-detected from this request</span>
                        }
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            data-testid="webhook-base-url-input"
                            type="url"
                            placeholder={webhook?.auto_detected_base_url || "https://your-domain.com"}
                            value={webhookBaseUrl}
                            onChange={(e) => setWebhookBaseUrl(e.target.value)}
                            className="flex-1 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm font-mono-num focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                        <button data-testid="save-webhook-url" onClick={saveWebhookUrl} disabled={savingUrl}
                            className="bg-[color:var(--bl-takealot-blue)] text-white px-3 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50 whitespace-nowrap">
                            {webhookBaseUrl.trim() ? (savingUrl ? "Saving…" : "Save URL") : "Use auto"}
                        </button>
                    </div>
                    <p className="text-[11px] text-[color:var(--bl-text-mute)] mt-1.5 leading-relaxed">
                        Override this when you deploy behind a custom domain (e.g. <code className="font-mono-num">https://insight.brotherscoffee.co.za</code>). Leave empty to use the auto-detected URL above. Re-paste the resulting full URL into Takealot whenever you change it.
                    </p>
                </div>

                <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">Full webhook URL — paste this into Takealot</label>
                    <div className="flex items-center gap-2">
                        <code data-testid="webhook-url" className="flex-1 font-mono-num text-xs bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 rounded-sm break-all">
                            {webhookFullUrl}
                        </code>
                        <button data-testid="copy-webhook-url" onClick={() => copy(webhookFullUrl)} className="bg-[color:var(--bl-takealot-blue)] text-white p-2 rounded-sm" title="Copy URL">
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-1">
                            Webhook secret {webhook?.configured && <span className="text-[color:var(--bl-success)] normal-case ml-1">· current: {webhook.secret_preview}</span>}
                        </label>
                        <input
                            data-testid="webhook-secret-input"
                            type="password"
                            placeholder="Paste the secret shown in Takealot"
                            value={webhookSecret}
                            onChange={(e) => setWebhookSecret(e.target.value)}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm font-mono-num focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button data-testid="save-webhook-secret" onClick={saveWebhookSecret} disabled={savingWebhook}
                            className="flex-1 bg-[color:var(--bl-takealot-blue)] text-white px-3 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50">
                            {savingWebhook ? "Saving…" : "Save secret"}
                        </button>
                        {webhook?.configured && (
                            <button data-testid="clear-webhook-secret" onClick={clearWebhookSecret} title="Remove"
                                className="px-3 py-2 text-sm border border-[color:var(--bl-danger)] text-[color:var(--bl-danger)] rounded-sm hover:bg-[color:var(--bl-danger)]/10">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3" data-testid="webhook-events">
                    {(webhook?.subscribed_events || []).map((e) => (
                        <span key={e} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm font-mono-num">{e}</span>
                    ))}
                </div>

                <div className="border-t border-[color:var(--bl-border)] pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Recent deliveries</div>
                        <button data-testid="refresh-deliveries" onClick={loadDeliveries} className="text-xs text-[color:var(--bl-takealot-blue)] hover:underline inline-flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" /> Refresh
                        </button>
                    </div>
                    {deliveries.length === 0 ? (
                        <div className="text-xs text-[color:var(--bl-text-dim)] py-3 text-center">No webhook deliveries yet. Trigger one by changing an offer in Takealot.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="webhook-deliveries-table">
                            <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                                <tr>
                                    <th className="text-left py-2">Received</th>
                                    <th className="text-left py-2">Event</th>
                                    <th className="text-left py-2">Signature</th>
                                    <th className="text-left py-2">Status</th>
                                    <th className="text-left py-2">From</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.slice(0, 20).map((d, i) => (
                                    <tr key={i} className="border-b border-[color:var(--bl-border)]">
                                        <td className="py-2 font-mono-num text-xs">{(d.received_at || "").slice(0, 19).replace("T", " ")}</td>
                                        <td className="py-2 font-mono-num text-xs">{d.event}</td>
                                        <td className="py-2 text-xs">
                                            {d.signature_valid === true && <span className="text-[color:var(--bl-success)]">✓ valid</span>}
                                            {d.signature_valid === false && <span className="text-[color:var(--bl-danger)]">✗ invalid</span>}
                                            {d.signature_valid == null && <span className="text-[color:var(--bl-text-dim)]">— not set</span>}
                                        </td>
                                        <td className="py-2 text-xs">
                                            {d.processed
                                                ? <span className="text-[color:var(--bl-success)]">processed</span>
                                                : <span className="text-[color:var(--bl-danger)]" title={d.error}>error</span>}
                                        </td>
                                        <td className="py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{d.remote_ip || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div data-testid="webhook-advisory-card" className="border border-[color:var(--bl-takealot-blue)]/40 bg-[color:var(--bl-takealot-blue)]/5 p-6 rounded-sm shadow-sm mb-6">
                <div className="flex items-start gap-3">
                    <Webhook className="w-5 h-5 text-[color:var(--bl-takealot-blue)] mt-1 shrink-0" />
                    <div className="flex-1">
                        <div className="font-serif-display text-xl text-[color:var(--bl-text)] mb-2">Sync orchestration — webhooks vs polling</div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                            <div className="p-3 bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm">
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Webhook health</div>
                                <div className={`mt-1 font-serif-display text-xl ${
                                    webhook?.health === "active" ? "text-[color:var(--bl-success)]" :
                                    webhook?.health === "stale" ? "text-[color:var(--bl-warning)]" :
                                    "text-[color:var(--bl-danger)]"
                                }`} data-testid="webhook-health">
                                    {webhook?.health === "active" ? "Active" : webhook?.health === "stale" ? "Stale" : webhook?.health === "inactive" ? "Inactive" : "Not received"}
                                </div>
                                <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-0.5 font-mono-num">
                                    {webhook?.last_received_at ? `last ${webhook.last_received_at.slice(0, 19).replace("T", " ")}` : "no deliveries yet"}
                                </div>
                            </div>
                            <div className="p-3 bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm">
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Auto-sync</div>
                                <div className={`mt-1 font-serif-display text-xl ${autoSync.enabled ? "text-[color:var(--bl-success)]" : "text-[color:var(--bl-text-dim)]"}`}>
                                    {autoSync.enabled ? `Every ${autoSync.interval_minutes < 60 ? autoSync.interval_minutes + "m" : (autoSync.interval_minutes / 60) + "h"}` : "Off"}
                                </div>
                                <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-0.5">window {autoSync.recent_days}d</div>
                            </div>
                            <div className="p-3 bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm">
                                <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Recommended</div>
                                <div className="mt-1 font-serif-display text-xl text-[color:var(--bl-takealot-orange)]" data-testid="orchestration-recommendation">
                                    {webhook?.health === "active" ? "Every 12h, 7d window" : webhook?.configured ? "Every 1h, 7d window" : "Every 1h, 30d window"}
                                </div>
                                <div className="text-[10px] text-[color:var(--bl-text-dim)] mt-0.5">
                                    {webhook?.health === "active" ? "Webhooks live — sync is safety net" : webhook?.configured ? "Webhooks configured but quiet — keep sync tight" : "No webhooks — sync is primary"}
                                </div>
                            </div>
                        </div>

                        <div className="text-sm text-[color:var(--bl-text-dim)] leading-relaxed mb-3 space-y-2">
                            <p>
                                <span className="font-semibold text-[color:var(--bl-text)]">The strategy</span> — webhooks are real-time but can be missed (network blip, downtime, Takealot retries). Polling catches what slipped through. Use both:
                            </p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li><span className="font-semibold text-[color:var(--bl-text)]">Webhooks = source of truth</span> for sale-status, offer changes, new orders — they push the moment things happen.</li>
                                <li><span className="font-semibold text-[color:var(--bl-text)]">Auto-sync = safety net</span> — wide enough to catch missed events, narrow enough to respect Takealot's rate limits (you have 200/window per endpoint).</li>
                                <li><span className="font-semibold text-[color:var(--bl-text)]">Manual sync = audit</span> — use the "Sync all-time" button quarterly to reconcile, or after Takealot fixes data on their side.</li>
                            </ol>
                            <p className="text-xs">
                                <strong>Bonus:</strong> The webhook handler always upserts by primary key (offer_id, order_item_id, etc.), so duplicate events from webhook + sync simply overwrite each other safely — no double counting.
                            </p>
                        </div>

                        <div className="text-xs text-[color:var(--bl-text-dim)] inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-[color:var(--bl-success)]/10 border border-[color:var(--bl-success)]/30">
                            <CheckCircle2 className="w-3 h-3 text-[color:var(--bl-success)]" />
                            Both are live. Adjust auto-sync interval above based on webhook health.
                        </div>
                    </div>
                </div>
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm mb-6">
                <div className="px-5 py-3 border-b border-[color:var(--bl-border)] font-serif-display text-xl flex items-center justify-between">
                    <span>External API access</span>
                    <span className="text-xs text-[color:var(--bl-text-dim)] font-sans tracking-normal">For other apps to read your data</span>
                </div>
                <div className="p-5">
                    <form onSubmit={createApiKey} className="flex gap-2 mb-4">
                        <input
                            data-testid="new-api-key-name"
                            type="text"
                            placeholder="Key name (e.g. Zapier, Make.com, internal-dashboard)"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                            className="flex-1 bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]"
                        />
                        <button
                            data-testid="create-api-key-btn"
                            type="submit"
                            disabled={creatingKey}
                            className="inline-flex items-center gap-1.5 bg-[color:var(--bl-takealot-blue)] text-white px-4 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50"
                        >
                            <Plus className="w-4 h-4" /> Generate
                        </button>
                    </form>

                    {createdKey && (
                        <div data-testid="new-api-key-banner" className="mb-4 p-4 border border-[color:var(--bl-takealot-orange)] bg-[color:var(--bl-takealot-orange)]/10 rounded-sm">
                            <div className="text-sm font-medium text-[color:var(--bl-text)] mb-1">Your new key — copy it now, it won't be shown again.</div>
                            <div className="flex items-center gap-2 mt-2">
                                <code className="flex-1 font-mono-num text-xs bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] px-3 py-2 rounded-sm break-all" data-testid="created-api-key-value">
                                    {createdKey.key}
                                </code>
                                <button
                                    data-testid="copy-api-key-btn"
                                    onClick={() => copy(createdKey.key)}
                                    className="bg-[color:var(--bl-takealot-blue)] text-white p-2 rounded-sm"
                                ><Copy className="w-4 h-4" /></button>
                                <button onClick={() => setCreatedKey(null)} className="text-xs text-[color:var(--bl-text-dim)]">Dismiss</button>
                            </div>
                            <div className="text-xs text-[color:var(--bl-text-dim)] mt-3">
                                Use header: <code className="font-mono-num">X-Insight-Api-Key: &lt;key&gt;</code> against any GET endpoint under <code className="font-mono-num">/api/</code>.
                            </div>
                        </div>
                    )}

                    {apiKeys.length === 0 ? (
                        <div className="text-sm text-[color:var(--bl-text-dim)] py-4 text-center">No external API keys yet.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="api-keys-table">
                            <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                                <tr>
                                    <th className="text-left py-2">Name</th>
                                    <th className="text-left py-2">Key</th>
                                    <th className="text-left py-2">Created</th>
                                    <th className="text-left py-2">Last used</th>
                                    <th className="text-left py-2">Status</th>
                                    <th className="text-right py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiKeys.map((k) => (
                                    <tr key={k.id} data-testid={`api-key-row-${k.name}`} className="border-b border-[color:var(--bl-border)]">
                                        <td className="py-2">{k.name}</td>
                                        <td className="py-2 font-mono-num text-xs">{k.key_preview}</td>
                                        <td className="py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{(k.created_at || "").slice(0, 10)}</td>
                                        <td className="py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{(k.last_used_at || "—").slice(0, 16).replace("T", " ")}</td>
                                        <td className="py-2">
                                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                                                k.revoked ? "bg-[color:var(--bl-danger)]/15 text-[color:var(--bl-danger)]" : "bg-[color:var(--bl-success)]/15 text-[color:var(--bl-success)]"
                                            }`}>{k.revoked ? "revoked" : "active"}</span>
                                        </td>
                                        <td className="py-2 text-right">
                                            {!k.revoked && (
                                                <button data-testid={`revoke-api-key-${k.name}`} onClick={() => revokeKey(k.id)} className="inline-flex items-center gap-1 text-xs text-[color:var(--bl-danger)] hover:underline">
                                                    <Trash2 className="w-3 h-3" /> Revoke
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm shadow-sm mb-6">
                <div className="px-5 py-3 border-b border-[color:var(--bl-border)] font-serif-display text-xl">Sync history</div>
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] border-b border-[color:var(--bl-border)]">
                        <tr>
                            <th className="text-left px-5 py-2">Resource</th>
                            <th className="text-right px-5 py-2">Records</th>
                            <th className="text-left px-5 py-2">Window</th>
                            <th className="text-left px-5 py-2">Last sync</th>
                        </tr>
                    </thead>
                    <tbody data-testid="sync-history-table">
                        {(!status?.sync_status || status.sync_status.length === 0) && (
                            <tr><td colSpan={4} className="text-center text-[color:var(--bl-text-dim)] py-6">No syncs yet.</td></tr>
                        )}
                        {status?.sync_status?.map((s) => (
                            <tr key={s.resource} className="border-b border-[color:var(--bl-border)]">
                                <td className="px-5 py-2">{s.resource}</td>
                                <td className="px-5 py-2 text-right font-mono-num">{s.count}</td>
                                <td className="px-5 py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{s.window || "—"}</td>
                                <td className="px-5 py-2 font-mono-num text-xs text-[color:var(--bl-text-dim)]">{(s.synced_at || "").replace("T", " ").slice(0, 19)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {status?.seller && (
                <div className="border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-6 rounded-sm shadow-sm" data-testid="seller-info">
                    <div className="font-serif-display text-xl mb-3">Seller account</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Display name</div>
                            <div>{status.seller.display_name}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Legal name</div>
                            <div>{status.seller.legal_name}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">Seller ID</div>
                            <div className="font-mono-num">{status.seller.seller_id}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-[color:var(--bl-text-dim)]">On vacation</div>
                            <div>{status.seller.on_vacation ? "Yes" : "No"}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* --------- Integrations: Resend --------- */}
            <div className="mt-10 border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] rounded-sm p-6 shadow-sm" data-testid="resend-section">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                    <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-[color:var(--bl-takealot-orange)]" />
                        <div>
                            <h3 className="font-serif-display text-xl">Email — Resend</h3>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)]">Welcome emails, password resets, sign-in reminders</div>
                        </div>
                    </div>
                    <div className="text-xs">
                        {resend?.configured ? (
                            <span className="inline-flex items-center gap-1.5 text-[color:var(--bl-success)] font-mono-num">
                                <CheckCircle2 className="w-4 h-4" /> Configured · key {resend.api_key_preview}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 text-[color:var(--bl-text-dim)]">
                                <AlertCircle className="w-4 h-4" /> Not configured
                            </span>
                        )}
                    </div>
                </div>

                <form onSubmit={saveResend} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-1">Resend API key</label>
                        <input data-testid="resend-api-key" type="password" placeholder="re_..." value={resendForm.api_key} onChange={(e) => setResendForm({ ...resendForm, api_key: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)] font-mono-num" />
                        <p className="text-[10px] text-[color:var(--bl-text-mute)] mt-1">Get one at resend.com → API Keys. Kept in Mongo so it rotates without redeploy.</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-1">Sender email</label>
                        <input data-testid="resend-sender-email" type="email" required placeholder="noreply@brotherscoffee.co.za" value={resendForm.sender_email} onChange={(e) => setResendForm({ ...resendForm, sender_email: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                        <p className="text-[10px] text-[color:var(--bl-text-mute)] mt-1">Domain must be verified in Resend (SPF + DKIM + DMARC DNS records).</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-1">App URL</label>
                        <input data-testid="resend-app-url" type="url" required placeholder="https://takealot.brotherscoffee.co.za" value={resendForm.app_url} onChange={(e) => setResendForm({ ...resendForm, app_url: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                        <p className="text-[10px] text-[color:var(--bl-text-mute)] mt-1">Used to build reset / setup links in outgoing emails.</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-1">Reply-to (optional)</label>
                        <input data-testid="resend-reply-to" type="email" placeholder="accounts@brotherscoffee.co.za" value={resendForm.reply_to} onChange={(e) => setResendForm({ ...resendForm, reply_to: e.target.value })}
                            className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                        <p className="text-[10px] text-[color:var(--bl-text-mute)] mt-1">Where replies to system emails land.</p>
                    </div>
                    <div className="md:col-span-2">
                        <button data-testid="resend-save" type="submit" disabled={savingResend} className="bg-[color:var(--bl-takealot-blue)] text-white px-4 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2">
                            {savingResend ? "Saving…" : "Save credentials"}
                        </button>
                    </div>
                </form>

                <div className="mt-6 pt-4 border-t border-[color:var(--bl-border)]">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bl-text-dim)] mb-2">Test the integration</div>
                    <div className="flex flex-wrap gap-2">
                        <input data-testid="resend-test-to" type="email" placeholder="you@yourdomain.com" value={testResendTo} onChange={(e) => setTestResendTo(e.target.value)}
                            className="flex-1 min-w-[240px] bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)]" />
                        <button data-testid="resend-test-btn" type="button" onClick={testResend} disabled={testingResend || !resend?.configured}
                            className="bg-[color:var(--bl-takealot-orange)] text-white px-4 py-2 text-sm rounded-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2">
                            <Send className="w-4 h-4" /> {testingResend ? "Sending…" : "Send test email"}
                        </button>
                    </div>
                </div>

                <div className="mt-4 text-[11px] text-[color:var(--bl-text-mute)] leading-relaxed">
                    <span className="font-medium text-[color:var(--bl-text-dim)]">Setup checklist:</span> 1) create the API key at resend.com · 2) add SPF, DKIM & DMARC records for your sending domain · 3) paste key + sender + app URL above · 4) click "Send test email" and check the inbox.
                </div>
            </div>
        </div>
    );
}
